import type WaButton from "@web.awesome.me/webawesome-pro/dist/components/button/button.js";
import type WaColorPicker from "@web.awesome.me/webawesome-pro/dist/components/color-picker/color-picker.js";
import type WaInput from "@web.awesome.me/webawesome-pro/dist/components/input/input.js";
import htmx from "htmx.org";
import { css, html } from "lit";
import { live } from "lit/directives/live.js";
import { z } from "zod";
import { checkNotNil } from "../../check-not-nil";
import { type FormSubmissionHandler, formSubmissionHandler } from "../../form";
import { JsonBootstrapper } from "../../handlers/json-bootstrapper";
import { stateProperty } from "../../property.js";
import { HexColor } from "../../schemas/color";
import {
  type ParserSchema,
  type ParserType,
  parser,
} from "../../schemas/parser";
import { Tag, TagColor } from "../../schemas/tag";
import { ValidationError } from "../../schemas/validation-error";
import { hostStyles } from "../../styles/host.styles";
import { WmElement } from "../element";

type Name = ParserType<typeof Name>;
const Name = stateProperty(z.string());

type Color = ParserType<typeof Color>;
const Color = stateProperty(HexColor.schema);

type BackgroundColor = ParserType<typeof BackgroundColor>;
const BackgroundColor = stateProperty(HexColor.schema);

const Payload = parser(
  z.strictObject({
    name: z.optional(z.string()),
  }),
);

export class WmTagCreate extends WmElement {
  static readonly TAG_NAME = "wm-tag-create";

  static properties = {
    backgroundColor: BackgroundColor,
    color: Color,
    name: Name,
  };

  static styles = [
    hostStyles,
    css`
      form {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: var(--wa-space-m);
      }

      wa-button {
        align-self: start;
      }

      .color {
        position: relative;
      }

      wm-clear-button {
        position: absolute;
        left: 3rem;
        top: 2rem;
      }
    `,
  ];

  declare backgroundColor: BackgroundColor;
  declare color: Color;
  declare name: Name;

  #formSubmit: FormSubmissionHandler;
  #jsonBootstrapper: JsonBootstrapper<ParserSchema<typeof Payload>>;

  constructor() {
    super();
    this.backgroundColor = undefined;
    this.color = undefined;
    this.name = undefined;
    this.#formSubmit = formSubmissionHandler("/app/tags", {
      method: "POST",
    })
      .on("pre:response", () => (this.#querySubmitButton().loading = true))
      .on("response", (r) => this.#handleResponse(r))
      .on("post:response", () => (this.#querySubmitButton().loading = false))
      .on("error", (err) => this.logger.error("unable to POST /app/tags", err));
    this.#jsonBootstrapper = new JsonBootstrapper({
      schema: Payload.schema,
      callback: (v) => {
        this.name = v.name;
      },
      logger: this.logger,
    });
  }

  protected override render() {
    return html`
      <slot @slotchange=${(e: Event) => this.#jsonBootstrapper.slotChanged(e)}></slot>
      <form @submit=${{ handleEvent: this.#formSubmit }}>
        <wa-input
          id="name"
          label="Name"
          name="name"
          required
          .value=${live(this.name)}
          @input=${this.#nameInput}>
        </wa-input>
        <div class="color">
          <wa-color-picker
            name="background_color"
            label="Background Color"
            hint="Customize the background color of your tags"
            format="hex"
            uppercase
            without-format-toggle
            .value=${live(this.backgroundColor)}
            @input=${this.#backgroundColorInput}>
          </wa-color-picker>
          <wm-clear-button @click=${this.#clearBackgroundColor} size="l"></wm-clear-button>
        </div>
        <div class="color">
          <wa-color-picker
            name="color"
            label="Text Color"
            hint="Customize the text color of your tags"
            format="hex"
            uppercase
            without-format-toggle
            .value=${live(this.color)}
            @input=${this.#colorInput}>
          </wa-color-picker>
          <wm-clear-button @click=${this.#clearColor} size="l"></wm-clear-button>
        </div>
        <wm-output label="Preview" appearance="plain">
          ${Tag.format({ name: this.name || "<type a name>", color: this.color, backgroundColor: this.backgroundColor })}
        </wm-output>
        <wa-button variant="success" type="submit">Create</wa-button>
      </form>
    `;
  }

  #nameInput(e: InputEvent) {
    e.stopPropagation();
    const input = checkNotNil(e.target) as WaInput;
    this.name = input.value;
    // Reset the custom validity, if any was set before.
    input.setCustomValidity("");
  }

  #colorInput(e: InputEvent) {
    e.stopPropagation();
    const picker = this.#queryColorPicker();
    picker.updateComplete.then(() => {
      this.color = TagColor.parse(picker.value);
    });
  }

  #backgroundColorInput(e: InputEvent) {
    e.stopPropagation();
    const picker = this.#queryBackgroundColorPicker();
    picker.updateComplete.then(() => {
      this.backgroundColor = TagColor.parse(picker.value);
    });
  }

  #clearBackgroundColor(e: MouseEvent) {
    e.stopPropagation();
    this.backgroundColor = undefined;
  }

  #clearColor(e: MouseEvent) {
    e.stopPropagation();
    this.color = undefined;
  }

  async #handleResponse(response: Response) {
    // Handle 422 with JSON serialized error messages.
    if (response.status === 422) {
      const validationError = ValidationError.parse(await response.json());
      const { path, reason } = validationError.error;

      if (path === "name") {
        const input = this.#queryNameInput();
        input.setCustomValidity(reason);
        input.reportValidity();
      } else {
        this.logger.warn(
          "unknown invalid field from 422 response: %s",
          path,
          validationError,
        );
      }
    } else {
      return htmx.ajax("GET", "/app/tags", { push: "true" });
    }
  }

  #queryNameInput(): WaInput {
    return this.queryNotNil("wa-input");
  }

  #queryColorPicker(): WaColorPicker {
    return this.queryNotNil(`wa-color-picker[name="color"]`);
  }

  #queryBackgroundColorPicker(): WaColorPicker {
    return this.queryNotNil(`wa-color-picker[name="background_color"]`);
  }

  #querySubmitButton(): WaButton {
    return this.queryNotNil(`wa-button[type="submit"]`);
  }
}

customElements.define(WmTagCreate.TAG_NAME, WmTagCreate);

declare global {
  interface HTMLElementTagNameMap {
    [WmTagCreate.TAG_NAME]: WmTagCreate;
  }
}
